import React, { useState, useEffect, useRef } from "react";
import Dialog from "../dialog";
import SelectBox from "../form/select-box";
import CircularProgressbar from "../circular-progressbar";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import '../styles/_our-solutions.module.scss';

const OpsAiSteps = () => {
    const { t } = useTranslation("common");
    const [activeTab, setActiveTab] = useState("");
    const [progress, setProgress] = useState(0);
    const [imageOpen, setImageOpen] = useState(false);
    const [imageURL, setImageURL] = useState("");
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const stepsData = t('product.ops-ai.steps', { returnObjects: true }) as any;
    const tabs = Array.isArray(stepsData?.tabs) ? stepsData.tabs : [];
    const content = Array.isArray(stepsData?.content) ? stepsData.content : [];
    const ctaData = stepsData?.cta || {};

    // Initialize activeTab with first tab from translations
    useEffect(() => {
        if (tabs.length > 0 && !activeTab) {
            setActiveTab(tabs[0]);
        }
    }, [tabs, activeTab]);

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setProgress(0);
    };

    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    const currentIndex = tabs.findIndex((t) => t === activeTab);
                    const nextIndex = (currentIndex + 1) % tabs.length;
                    setActiveTab(tabs[nextIndex]);
                    return 0;
                }
                return prev + 1;
            });
        }, 150);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [activeTab, tabs]);

    const renderContent = () => {
        const tabContent = content.find((tab) => tab.name === activeTab);
        if (!tabContent) return null;

        return (
            <div className={"product-content ops-ai-tab"}>
                <div className={"d-flex"}>
                    <div className={"left-block"}>
                        <p className={"steps"}>{tabContent.name}</p>
                        <div className={"d-flex"}>
                            <h3>{tabContent.title}</h3>
                        </div>
                        <p>{tabContent.description}</p>
                    </div>
                    <div className={"right-block"}>
                        <img
                            src={tabContent.image}
                            alt={tabContent.name}
                            width={100}
                            height={100}
                            onClick={() => imageDialog(tabContent.image)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    function imageDialog(url: string) {
        setImageOpen(true);
        setImageURL(url);
    }

    return (
        <>
            <div className={"our-solutions ops-ai-tabs"}>
                <div className={"home-product-tab"}>
                    <div className="container">
                        <div className="tab-content">
                            <div className={"d-flex"}>
                                <div className="top-tabs">
                                    <ul>
                                        {tabs.map((tab) => (
                                            <li
                                                key={tab}
                                                className={activeTab === tab ? "active" : ""}
                                                onClick={() => handleTabChange(tab)}
                                            >
                                                <div className="tab-label">
                                                    <p>{tab}</p>
                                                    {activeTab === tab && (
                                                        <CircularProgressbar progress={progress} defaultColor={"#5b5fc7"} />
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                    <SelectBox
                                        items={tabs.map((tab) => ({ label: tab, value: tab }))}
                                        defaultSelectedItemIndex={tabs.indexOf(activeTab)}
                                        onChange={(e) => handleTabChange(e.value)}
                                    />

                                    <div className="signup-btns">
                                        <Link href={"https://app.middleware.io/auth/register/"} target="_blank" className="sign-up ahref-btn primary">Configure OpsAI Now</Link>
                                    </div>
                                </div>
                                <div className={"right-content"}>{renderContent()}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                isOpen={imageOpen}
                position={"center"}
                onClose={() => {
                    setImageOpen(false);
                    setImageURL("");
                }}
                size={"md"}
                verticalCenter={true}
            >
                <div>
                    <img src={imageURL} alt={"OpsAI Image"} width={100} height={100} />
                </div>
            </Dialog>
        </>
    );
};

export default OpsAiSteps;
